import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import {
  Archive,
  ArrowDownLeft as ArrowDownLeftIcon,
  ArrowUp,
  ArrowUpRight as ArrowUpRightIcon,
  Clock,
  Group as GroupIcon,
  MailIn,
  MoreVert,
  Prohibition,
} from 'iconoir-react'
import { ReactElement, useState } from 'react'
import { Dropdown } from '../shared/styled/Dropdown'
import { AIBadgePill, AIBadgePillDisabled } from '../Icon/AIBadgeIcon'
import { OverflowText } from '../shared/styled/OverflowText'
import { Tooltip } from 'app/components/Tooltip'
import { Badge } from 'app/components/ui/badge'
import { formatTimestamp } from 'app/date.utils'
import { isFailedDelivery, isOutboundMessage } from 'app/lib/messageDelivery'
import type { MessageJson } from 'app/types/entities'
import { usPhoneNumber } from 'lib/phone'
import { formatPhoneNumber } from 'app/formatPhoneNumber'

function formatPhone(value?: string): string | undefined {
  if (!value) return undefined
  const us = usPhoneNumber(value)
  return us ? formatPhoneNumber(us) : value
}

export function Conversation({
  message,
  relatedContacts,
  name,
  contactPhone,
  isActive,
  hasUnseenUpdates,
  compact,
  agentDisabled,
  optedOut,
  scheduledCount = 0,
  onClick,
  onArchive,
  onMarkUnread,
}: {
  message: MessageJson & { unread: number; hasAI?: boolean }
  relatedContacts?: string[]
  name?: string
  contactPhone?: string
  isActive?: boolean
  hasUnseenUpdates?: boolean
  compact?: boolean
  /**
   * When true, the AI pill (shown only when this thread has prior AI activity)
   * renders the muted "AI Disabled" variant instead of the active blue pill.
   * Derived from `Thread.aiEnabled === false` upstream.
   */
  agentDisabled?: boolean
  /**
   * The contact holds an opt-out record at this location. Same `optOutFor`
   * lookup the list partition uses, so a badged thread and a thread kept out of
   * the working list are always the same set.
   */
  optedOut?: boolean
  /**
   * How many held messages are waiting to send on this thread. Adds a clock next
   * to the timestamp only: the preview and the timestamp itself keep showing the
   * last REAL message, so a queued send never moves or explains a row's position
   * in a newest-sorted list. Counted rather than flagged because nothing stops
   * two staff each queueing one, and a row that says "One message scheduled"
   * over two of them sends the user to the tray expecting the wrong thing.
   */
  scheduledCount?: number
  onClick: () => void
  onArchive: (message: MessageJson) => void
  onMarkUnread: (message: MessageJson) => void
}): ReactElement {
  // Keeps the kebab visible while its menu is open: the pointer moving into the
  // portaled menu leaves the row, which would otherwise drop group-hover and
  // hide the trigger under the open menu.
  const [menuOpen, setMenuOpen] = useState(false)
  const phone = contactPhone || (message.sentByUserId || message.isAgent ? message.to : message.from)
  // The name prop is already sanitized upstream (backend "Unknown" stripped, user
  // overrides passed through verbatim) — show whatever we're given or fall back
  // to the formatted phone number.
  const hasName = !!name
  const displayName = hasName ? name! : (formatPhone(phone) ?? `Unknown`)
  const contactCount = relatedContacts?.length ?? 0
  const isUnread = (message.unread ?? 0) > 0
  const scheduledLabel = `${scheduledCount} message${scheduledCount === 1 ? `` : `s`} scheduled`
  // `message` is the thread's latest message, so this is the iMessage rule:
  // alert the row while an undelivered outbound is the last word in the thread.
  const lastMessageFailed = isFailedDelivery(message)
  // Same latest-message rule: which way the last word went. Outbound covers both
  // staff replies and AI/workflow sends (the AI pill separates those two).
  const lastMessageOutbound = isOutboundMessage(message)

  if (compact) {
    const letters = displayName.replace(/[^A-Za-z]/g, ``)
    const initials = hasName
      ? displayName
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((p) => p[0]!.toUpperCase())
          .join(``)
          .slice(0, 2)
      : letters
        ? letters.slice(0, 2).toUpperCase()
        : (phone ?? ``).replace(/\D/g, ``).slice(-2)
    return (
      <button
        type="button"
        onClick={onClick}
        title={displayName}
        aria-label={displayName}
        className={classNames(
          `relative flex h-14 w-full cursor-pointer items-center justify-center border-b border-subtle transition-colors`,
          isActive ? `bg-surface-muted` : `bg-transparent hover:bg-surface-muted`,
        )}
      >
        {isActive && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-inverse" />}
        <span
          className={classNames(
            `relative flex h-10 w-10 items-center justify-center rounded-full text-label-xs font-semibold`,
            isActive
              ? `bg-inverse text-inverse`
              : isUnread
                ? `bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200`
                : `bg-surface-muted text-default`,
          )}
        >
          {initials || `?`}
          {isUnread && !isActive && (
            <span
              aria-label="Unread"
              className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary-600 ring-2 ring-(--surface)"
            />
          )}
          {lastMessageFailed && (
            <span
              aria-label="Last message not delivered"
              className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-(--surface)"
            />
          )}
        </span>
      </button>
    )
  }

  return (
    // The row itself is a button, so the archive control cannot live inside it:
    // nesting a button in a button is invalid HTML. They are siblings under this
    // wrapper instead, which also owns the `group` hover state.
    <div className="group relative w-full">
      <button
        type="button"
        onClick={onClick}
        className={classNames(
          `relative flex w-full cursor-pointer items-start gap-x-3 border-b border-subtle px-4 py-4 text-left transition-colors`,
          isActive ? `bg-surface-muted` : `bg-transparent hover:bg-surface-muted`,
        )}
      >
        {isActive && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-inverse" />}

        <div className="flex min-w-0 flex-1 flex-col gap-y-1">
          <div className="flex items-center gap-x-2">
            <OverflowText
              className={classNames(
                `min-w-0 flex-shrink truncate text-heading-xs font-semibold`,
                isUnread ? `text-strong` : `text-default`,
              )}
              content={displayName}
              hasTooltip
            />
            {isUnread && <Badge label="Unread" color="blue" className="shrink-0" />}
            {contactCount > 1 && (
              <span className="inline-flex shrink-0 items-center gap-x-1 rounded border border-default bg-surface px-1.5 py-0.5 text-label-xs text-subtle">
                <GroupIcon className="h-3 w-3" strokeWidth={2} />
                {contactCount}
              </span>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-x-1.5">
              {lastMessageFailed && (
                <Tooltip message="Last message not delivered" placement="top" className="flex shrink-0">
                  <ExclamationTriangleIcon
                    role="img"
                    aria-label="Last message not delivered"
                    className="h-4 w-4 text-red-500"
                  />
                </Tooltip>
              )}
              {message.hasAI && (agentDisabled ? <AIBadgePillDisabled /> : <AIBadgePill />)}
              {scheduledCount > 0 && (
                <Tooltip message={scheduledLabel} placement="top" className="flex shrink-0">
                  {/* Primary, not a neutral: it marks a live queued message, and at
                      this size a grey glyph beside the timestamp reads as chrome and
                      goes unseen. */}
                  <Clock
                    role="img"
                    aria-label={scheduledLabel}
                    className="h-3.5 w-3.5 text-primary-600"
                    strokeWidth={2}
                  />
                </Tooltip>
              )}
              <span
                className={classNames(
                  `whitespace-nowrap text-label-xs tabular-nums`,
                  isUnread ? `font-medium text-default` : isActive ? `text-default` : `text-subtle`,
                )}
              >
                {formatTimestamp(message.createdDate)}
              </span>
            </div>
          </div>

          <div className="flex min-w-0 items-start gap-x-2">
            {/*
            Direction chip, or the opt-out mark when this contact has opted out.
            Opt-out takes the slot on purpose: on a thread nobody can reply to,
            who spoke last is the less useful fact, and this mark is what tells
            an opted-out row apart from one somebody archived by hand.
            Filled chip = they spoke last (your move); outlined = we spoke last.
          */}
            <Tooltip
              message={
                optedOut ? `Opted out of SMS` : lastMessageOutbound ? `Last message sent` : `Last message received`
              }
              placement="top"
              className="flex shrink-0"
            >
              <span
                role="img"
                aria-label={
                  optedOut ? `Opted out of SMS` : lastMessageOutbound ? `Last message sent` : `Last message received`
                }
                className={classNames(
                  `mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors`,
                  optedOut
                    ? `bg-red-100 text-red-600 ring-1 ring-inset ring-red-200`
                    : lastMessageOutbound
                      ? `text-minimal ring-1 ring-inset ring-strong`
                      : isUnread
                        ? `bg-primary-100 text-primary-700`
                        : `bg-surface-strong text-default`,
                )}
              >
                {optedOut ? (
                  <Prohibition className="h-2.5 w-2.5" strokeWidth={2.75} />
                ) : lastMessageOutbound ? (
                  <ArrowUpRightIcon className="h-2.5 w-2.5" strokeWidth={2.75} />
                ) : (
                  <ArrowDownLeftIcon className="h-2.5 w-2.5" strokeWidth={2.75} />
                )}
              </span>
            </Tooltip>
            <OverflowText
              className={classNames(
                `min-w-0 line-clamp-2 text-body-xs leading-snug`,
                isUnread ? `text-default` : `text-subtle`,
              )}
              content={message.text ?? ``}
            />
          </div>
        </div>
      </button>

      {/*
        Revealed on hover, and reachable by keyboard via focus-visible. It sits
        over the timestamp, the same trade every mail client makes: the date is
        reference information, the action is what you came for.
      */}
      <div
        className={classNames(
          `absolute right-3 top-1/2 -translate-y-1/2 focus-within:flex group-hover:flex`,
          menuOpen ? `flex` : `hidden`,
        )}
      >
        <Dropdown
          placement="bottom-end"
          closeOnClick
          getOpenState={setMenuOpen}
          button={
            <button
              type="button"
              aria-label="Conversation actions"
              title="More actions"
              className="flex items-center justify-center rounded-md border border-default bg-surface p-1.5 text-subtle shadow-sm transition-colors hover:bg-surface-subtle hover:text-strong"
            >
              <MoreVert className="h-4 w-4" strokeWidth={2} />
            </button>
          }
        >
          <div className="py-1">
            <button
              type="button"
              onClick={() => onMarkUnread(message)}
              className="flex w-full items-center gap-x-2 px-3 py-2 text-left text-label-xs text-default hover:bg-surface-subtle"
            >
              <MailIn className="h-3.5 w-3.5" strokeWidth={2} />
              Mark as unread
            </button>
            <button
              type="button"
              onClick={() => onArchive(message)}
              className="flex w-full items-center gap-x-2 px-3 py-2 text-left text-label-xs text-default hover:bg-surface-subtle"
            >
              {message.archived ? (
                <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
              ) : (
                <Archive className="h-3.5 w-3.5" strokeWidth={2} />
              )}
              {message.archived ? `Unarchive` : `Archive`}
            </button>
          </div>
        </Dropdown>
      </div>
    </div>
  )
}
